import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { etudiantsAPI } from '../services/api';
import { departementCourant, peutModifier } from '../services/droits';
import { analyserFeuilleScodoc, analyserCollageScodoc } from '../services/scodocImport';
import { normaliserIntitule } from '../services/apogeeImport';
import { libelleChamp } from '../services/etudiantsAffichage';

/**
 * MODULE ÉTUDIANTS — onglet 2, IMPORT SCODOC — RANG (lot 1.4)
 * ====================================================
 * Spec     : doc/modules/etudiants.md §0.5 · §0.7 · §0.8 · §Onglet 2 section B
 * Maquette : doc/maquettes/etudiants_maquette_v5.html #tab-import, colonne de droite
 * Moteur   : services/scodocImport.js — appariement d'après le MAPPING paramétrable
 *
 * ── CE QUE LA MAQUETTE IMPOSE, ET QUE LA SPEC NE DIT PAS ─────────────────────
 *   · la garantie est dans la BARRE DE TITRE — « Ne crée pas · Ne modifie que
 *     classement » — et non enterrée dans une aide ;
 *   · un BANDEAU JAUNE PERMANENT sous la zone d'import redit ce que l'import ne
 *     touche pas. La spec énonce la règle, la maquette l'affiche ;
 *   · le guide de collage n'a que TROIS étapes, contre quatre pour APOGEE : il n'y a
 *     pas de fichier Excel à ouvrir, on copie depuis l'interface SCODOC ;
 *   · « Non trouvés » est en ORANGE partout — bilan, détail, et jusqu'à l'icône
 *     d'en-tête de l'historique. Un candidat jamais inscrit est le cas NORMAL (§0.5),
 *     et le rouge apprendrait à ignorer les alertes ;
 *   · l'historique n'a AUCUNE colonne « Créés ». C'est la preuve visuelle que cet
 *     import ne crée rien, et elle est gardée telle quelle ;
 *   · le détail d'un non-trouvé nomme la VALEUR DE LA CLÉ (« Code NIP 20249999
 *     inconnu »), pas seulement un numéro de ligne.
 *
 * ── UN ÉCART, ET IL EST ARITHMÉTIQUE (E39) ───────────────────────────────────
 * La maquette montre TROIS compteurs (Mis à jour / Non trouvés / Erreurs). Il en faut
 * QUATRE, parce que la v38 a créé `nb_ignores` en le destinant explicitement à cet
 * import : « ligne appariée sans classement à apporter ». Sur le fichier réel elles
 * sont ONZE. Avec trois compteurs, 59 lignes appariées donneraient « 48 mis à jour »
 * et les onze autres disparaîtraient — les chiffres ne s'additionneraient plus, et
 * l'utilisateur chercherait onze étudiants perdus qui ne le sont pas.
 *
 * ── CE QUE CET IMPORT NE FAIT JAMAIS ─────────────────────────────────────────
 * Il ne crée ni personne ni inscription, et ne touche aucun champ d'APOGEE. La liste
 * blanche est tenue par le SERVEUR (`CHAMPS_SCODOC_AUTORISES`) : ce que cet écran
 * envoie est filtré à l'arrivée, et une clé inconnue n'écrit rien.
 */

/** Extensions lues. Comme APOGEE : la scolarité envoie un classeur, jamais un CSV. */
const EXTENSIONS = ['xlsx', 'xlsm', 'xls'];

const messageErreur = (e, repli) => e?.response?.data?.error || e?.message || repli;

/** « 16/08/2026 10h32 » — la forme de l'en-tête de bilan de la maquette. */
function dateHeure(valeur) {
    if (!valeur) return '';
    const d = new Date(valeur);
    if (Number.isNaN(d.getTime())) return '';
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}h${p(d.getMinutes())}`;
}

/** Nombre de lignes collées, en-tête comprise. */
function lignesCollees(texte) {
    return String(texte || '').replace(/\r\n/g, '\n').split('\n').filter((l) => l.trim() !== '').length;
}

/** Les motifs que le serveur ne compte PAS comme des erreurs. Miroir exact de
 *  `MOTIFS_NON_BLOQUANTS_SCODOC` : deux listes divergentes, et l'écran compterait des
 *  erreurs que le journal ne compte pas. */
const MOTIFS_AVERTISSEMENT = ['non_trouve', 'sans_inscription', 'verrou_actif'];

// ═════════════════════════════════════════════════════════════════════════════
// LA COLONNE
// ═════════════════════════════════════════════════════════════════════════════
// `avant` (lot E.3) : ce qui vient en tête de la colonne SCODOC — la carte « Import SCODOC — Étudiants ».
function ImportScodoc({ user, config, actif, anneeId, annees, avant = null }) {
    const departementId = departementCourant(user, config);
    const modifiable = peutModifier(user, departementId, 'etudiants');

    const [dejaCharge, setDejaCharge] = useState(false);
    const [reglages, setReglages] = useState(null);
    const [champsScodoc, setChampsScodoc] = useState(null);
    const [historique, setHistorique] = useState([]);

    // Étape : 'depot' → 'apercu' → 'bilan'
    const [etape, setEtape] = useState('depot');
    // ⚠️ Le mode par défaut est COLLER, contrairement à la colonne APOGEE. C'est le
    // geste réel de la scolarité : l'export « SCODOC RANG » n'a que deux colonnes
    // utiles, on les copie. Le mode fichier reste disponible d'un clic — il est
    // écrit et recetté, et le jour où un .xlsx arrive il n'y a rien à redemander.
    const [mode, setMode] = useState('coller');
    const [colle, setColle] = useState('');
    const [occupe, setOccupe] = useState(false);
    const [erreur, setErreur] = useState(null);
    const [survol, setSurvol] = useState(false);

    const classeur = useRef(null);
    const xlsx = useRef(null);
    const [fichierNom, setFichierNom] = useState(null);
    const [feuilles, setFeuilles] = useState([]);
    const [feuille, setFeuille] = useState('');

    const [analyse, setAnalyse] = useState(null);   // moteur local
    const [apercu, setApercu] = useState(null);     // ce que le serveur ferait
    const [bilan, setBilan] = useState(null);
    const [detailsOuverts, setDetailsOuverts] = useState(false);

    /**
     * Le semestre du relevé. ⚠️ Il n'est PAS remis à zéro par « Annuler » ni par un
     * changement de mode : importer les relevés d'un même semestre l'un après l'autre
     * est le geste normal, et le redemander à chaque fois serait une friction pour
     * rien. Il n'est exigé que si le fichier apporte un rang de promotion.
     */
    const [semestre, setSemestre] = useState('');

    const anneeActive = (annees || []).find((a) => a.id === anneeId) || null;
    const cleLiaison = reglages?.scodoc_cle_liaison || 'code_nip';

    /**
     * Les trois réglages dont le moteur a besoin, réunis en un objet STABLE.
     *
     * ⚠️ Le `useMemo` n'est pas une optimisation : `reglages?.x || {}` fabrique un
     * objet neuf à chaque rendu, ce qui rendrait `analyserClasseur` et `lireFichier`
     * instables. C'est la famille de défaut qui a effacé la saisie en cours au lot
     * 1.3 bis — une dépendance recréée à chaque rendu, dans un `useCallback`. Ici
     * elle ne recharge rien, mais on ne laisse pas le piège en place.
     */
    const mapping = useMemo(() => reglages?.scodoc_colonnes_mapping || {}, [reglages]);
    const nbCibles = Object.keys(mapping).length;
    const optionsMoteur = useMemo(
        () => ({ cleLiaison, mapping, champsAutorises: champsScodoc }),
        [cleLiaison, mapping, champsScodoc]
    );

    // ── Chargement à la première ouverture de l'onglet ────────────────────────
    const charger = useCallback(async () => {
        if (!departementId) { setErreur('Aucun département sélectionné'); return; }
        try {
            const [param, imports] = await Promise.all([
                etudiantsAPI.getParametrage(departementId),
                etudiantsAPI.getImports(departementId, 'scodoc')
            ]);
            setReglages(param.data.reglages);
            setChampsScodoc(param.data.champsScodoc || {});
            setHistorique(imports.data.imports || []);

            // Le bilan du DERNIER import, affiché en permanence comme la maquette : un
            // écran vide au retour laisserait croire qu'aucun import n'a jamais eu lieu.
            const dernier = (imports.data.imports || [])[0];
            if (dernier) setBilan(bilanDepuisJournal(dernier));
        } catch (e) {
            setErreur(messageErreur(e, "Impossible de lire l'état des imports SCODOC"));
        }
    }, [departementId]);

    useEffect(() => {
        if (actif && !dejaCharge) { setDejaCharge(true); charger(); }
    }, [actif, dejaCharge, charger]);

    // ── Lecture du fichier ────────────────────────────────────────────────────
    const analyserClasseur = useCallback((nomFeuille) => {
        const XLSX = xlsx.current;
        const wb = classeur.current;
        if (!XLSX || !wb) return;
        // `raw: true` comme pour APOGEE : le numéro étudiant peut arriver en nombre, et
        // laisser SheetJS le convertir en texte d'affichage perdrait sa forme.
        const matrice = XLSX.utils.sheet_to_json(wb.Sheets[nomFeuille], {
            header: 1, raw: true, defval: null, blankrows: true
        });
        setFeuille(nomFeuille);
        setAnalyse(analyserFeuilleScodoc(matrice, optionsMoteur));
        setApercu(null);
        setEtape('apercu');
    }, [optionsMoteur]);

    const lireFichier = useCallback(async (fichier) => {
        setErreur(null);
        const ext = (fichier.name.split('.').pop() || '').toLowerCase();
        if (!EXTENSIONS.includes(ext)) {
            setErreur(`Format non supporté : « .${ext} ». Attendu un classeur ${EXTENSIONS.map((e) => '.' + e).join(', ')}`);
            return;
        }
        setOccupe(true);
        try {
            const XLSX = await import('xlsx');
            const donnees = new Uint8Array(await fichier.arrayBuffer());
            const wb = XLSX.read(donnees, { type: 'array' });
            xlsx.current = XLSX;
            classeur.current = wb;
            setFichierNom(fichier.name);
            setFeuilles(wb.SheetNames || []);
            setBilan(null);

            // Même choix de feuille qu'APOGEE, et pour la même raison : on retient
            // celle qui porte la colonne clé ET des données. L'export SCODOC n'a qu'une
            // feuille, mais le classeur de la scolarité en a deux — et déposer le
            // mauvais des deux fichiers est le geste le plus facile à faire.
            const jaugee = (wb.SheetNames || []).map((nom) => {
                const m = XLSX.utils.sheet_to_json(wb.Sheets[nom], { header: 1, raw: true });
                const entetes = m[0] || [];
                const aLaCle = entetes.some(
                    (h) => normaliserIntitule(h) === normaliserIntitule(cleLiaison)
                );
                const nbDonnees = m.slice(1).filter(
                    (l) => (l || []).some((c) => String(c ?? '').trim() !== '')
                ).length;
                return { nom, aLaCle, nbDonnees };
            });
            const retenue = jaugee.find((f) => f.aLaCle && f.nbDonnees > 0)
                         || jaugee.find((f) => f.aLaCle)
                         || jaugee[0];
            analyserClasseur((retenue && retenue.nom) || wb.SheetNames[0]);
        } catch (e) {
            setErreur(messageErreur(e, "Ce fichier n'a pas pu être lu"));
        } finally {
            setOccupe(false);
        }
    }, [cleLiaison, analyserClasseur]);

    const surDepot = (e) => {
        e.preventDefault();
        setSurvol(false);
        if (!modifiable) return;
        const fichier = e.dataTransfer?.files?.[0];
        if (fichier) lireFichier(fichier);
    };

    // ── Prévisualisation côté serveur ─────────────────────────────────────────
    const demanderApercu = useCallback(async () => {
        if (!analyse || analyse.bloquant) return;
        setOccupe(true);
        setErreur(null);
        try {
            const { data } = await etudiantsAPI.apercuImportScodoc(
                departementId, anneeId, analyse.lignes, semestre || null);
            setApercu(data);
        } catch (e) {
            setErreur(messageErreur(e, "L'aperçu a échoué"));
        } finally {
            setOccupe(false);
        }
        // `semestre` est dans les dépendances : le changer doit refaire l'aperçu,
        // sinon l'écran annoncerait un semestre et en écrirait un autre.
    }, [analyse, departementId, anneeId, semestre]);

    useEffect(() => {
        if (etape === 'apercu' && analyse && !analyse.bloquant && !apercu && !occupe) {
            demanderApercu();
        }
    }, [etape, analyse, apercu, occupe, demanderApercu]);

    // ── L'import ──────────────────────────────────────────────────────────────
    const importer = useCallback(async () => {
        if (!analyse || analyse.bloquant) return;
        setOccupe(true);
        setErreur(null);
        try {
            const { data } = await etudiantsAPI.importerScodoc(
                departementId, anneeId, analyse.lignes,
                mode === 'coller' ? null : fichierNom, mode, semestre || null
            );
            setBilan(data);
            setEtape('bilan');
            const imports = await etudiantsAPI.getImports(departementId, 'scodoc');
            setHistorique(imports.data.imports || []);
        } catch (e) {
            setErreur(messageErreur(e, "L'import a échoué"));
        } finally {
            setOccupe(false);
        }
    }, [analyse, departementId, anneeId, fichierNom, mode, semestre]);

    const recommencer = useCallback(() => {
        classeur.current = null;
        setFichierNom(null);
        setFeuilles([]);
        setFeuille('');
        setAnalyse(null);
        setApercu(null);
        setErreur(null);
        setColle('');
        setDetailsOuverts(false);
        setEtape('depot');
    }, []);

    /** Changer de semestre invalide l'aperçu : il annonçait l'ancien. */
    const changerSemestre = useCallback((valeur) => {
        setSemestre(valeur);
        setApercu(null);
    }, []);

    const changerMode = useCallback((nouveau) => {
        if (nouveau === mode) return;
        recommencer();
        setMode(nouveau);
    }, [mode, recommencer]);

    const analyserLeCollage = useCallback(() => {
        setErreur(null);
        setApercu(null);
        setAnalyse(analyserCollageScodoc(colle, optionsMoteur));
        setEtape('apercu');
    }, [colle, optionsMoteur]);

    // ═════════════════════════════════════════════════════════════════════════
    // RENDU
    // ═════════════════════════════════════════════════════════════════════════
    return (
        <div className="etu-import-colonne">
            {avant}

            <div className="etu-carte">
                <div className="etu-carte-entete etu-entete-scodoc">
                    <i className="fa-solid fa-ranking-star" />
                    <span>Import SCODOC — Rangs (admission et promotion)</span>
                    {/* La garantie EST le sous-titre : c'est la forme de la maquette. */}
                    <span className="etu-entete-note">Ne crée pas · Ne modifie que les rangs</span>
                </div>

                <div className="etu-import-corps">

                    {!modifiable && (
                        <div className="etu-alerte etu-alerte-info">
                            <i className="fa-solid fa-eye" />
                            <span>
                                Lecture seule : ton compte peut consulter ce module, pas y écrire.
                                L'historique et le dernier bilan restent visibles.
                            </span>
                        </div>
                    )}

                    {/* Le pendant de l'alerte « aucun code mappé » d'APOGEE : sans
                        correspondance de colonne, cet import n'écrirait rien du tout, et
                        le dire AVANT le dépôt évite de chercher la cause dans le fichier. */}
                    {champsScodoc && nbCibles === 0 && (
                        <div className="etu-alerte etu-alerte-warning">
                            <i className="fa-solid fa-triangle-exclamation" />
                            <span>
                                Aucune colonne SCODOC n'est associée à un champ de l'application :
                                l'import <strong>n'écrirait rien</strong>. À régler dans l'onglet{' '}
                                <strong>Paramétrage</strong>, carte « Mapping colonnes SCODOC ».
                            </span>
                        </div>
                    )}

                    {erreur && (
                        <div className="etu-statut etu-statut-ko etu-statut-bloc">
                            <i className="fa-solid fa-circle-exclamation" />
                            <span>{erreur}</span>
                        </div>
                    )}

                    {/* ── Le sélecteur de mode ── */}
                    <div className="etu-modes">
                        <button
                            type="button"
                            className={'etu-mode etu-mode-scodoc' + (mode === 'fichier' ? ' active' : '')}
                            onClick={() => changerMode('fichier')}
                        >
                            <i className="fa-solid fa-file-excel" /> Fichier .xlsx
                        </button>
                        <button
                            type="button"
                            className={'etu-mode etu-mode-scodoc' + (mode === 'coller' ? ' active' : '')}
                            onClick={() => changerMode('coller')}
                        >
                            <i className="fa-solid fa-clipboard" /> Coller (Ctrl+V)
                        </button>
                    </div>

                    {/* ── Le collage : TROIS étapes, pas quatre ──
                        Il n'y a pas de fichier Excel à ouvrir, on copie le tableau
                        directement depuis l'interface SCODOC. La consigne des en-têtes
                        est en revanche la même (écart E28) : sans la ligne d'intitulés,
                        les colonnes ne pourraient être identifiées que par leur
                        position — et ici les intitulés sont ceux du MAPPING, donc
                        indispensables. */}
                    {etape === 'depot' && mode === 'coller' && (
                        <div className="etu-coller">
                            <div className="etu-coller-guide">
                                <span className="etu-coller-etape">
                                    <span className="etu-coller-num etu-coller-num-scodoc">1</span> Interface SCODOC
                                </span>
                                <i className="fa-solid fa-chevron-right etu-coller-fleche" />
                                <span className="etu-coller-etape">
                                    <span className="etu-coller-num etu-coller-num-scodoc">2</span> Copier le tableau,
                                    <strong>&nbsp;en-têtes comprises</strong>
                                </span>
                                <i className="fa-solid fa-chevron-right etu-coller-fleche" />
                                <span className="etu-coller-etape">
                                    <span className="etu-coller-num etu-coller-num-scodoc">3</span> <kbd>Ctrl+V</kbd> ici
                                </span>
                            </div>

                            <textarea
                                className="etu-coller-zone"
                                value={colle}
                                onChange={(e) => setColle(e.target.value)}
                                disabled={!modifiable || occupe}
                                placeholder={'Collez ici (Ctrl+V)…\n\nLa première ligne collée doit être celle des intitulés de colonnes.'}
                                spellCheck={false}
                            />

                            <div className="etu-coller-pied">
                                <span className="etu-aide">
                                    {colle.trim()
                                        ? `${lignesCollees(colle)} ligne${lignesCollees(colle) > 1 ? 's' : ''} collée${lignesCollees(colle) > 1 ? 's' : ''}, en-tête comprise`
                                        : 'Aucun contenu collé'}
                                </span>
                                <select
                                    value={anneeId || ''}
                                    disabled
                                    title="L'import porte toujours sur l'année active."
                                >
                                    <option value={anneeId || ''}>{anneeActive?.libelle || '—'}</option>
                                </select>
                                <button
                                    type="button"
                                    className="etu-btn etu-btn-scodoc"
                                    disabled={!modifiable || occupe || !colle.trim()}
                                    onClick={analyserLeCollage}
                                >
                                    <i className="fa-solid fa-eye" /> Aperçu &amp; importer
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── Le dépôt ── */}
                    {etape === 'depot' && mode === 'fichier' && (
                        <div
                            className={'etu-depot' + (survol ? ' survol' : '')}
                            onDragOver={(e) => { e.preventDefault(); setSurvol(true); }}
                            onDragLeave={() => setSurvol(false)}
                            onDrop={surDepot}
                        >
                            <i className="fa-solid fa-file-excel etu-depot-icone" />
                            <div className="etu-depot-texte">
                                Glissez votre <code>.xlsx</code> ici ou
                            </div>
                            {/* Verrouillé sur l'année active, comme APOGEE (écart E25). */}
                            <select
                                value={anneeId || ''}
                                disabled
                                title="L'import porte toujours sur l'année active. Pour en changer, il faut changer l'année active."
                            >
                                <option value={anneeId || ''}>{anneeActive?.libelle || '—'}</option>
                            </select>
                            <label className={'etu-btn etu-btn-scodoc' + (modifiable ? '' : ' etu-btn-inerte')}>
                                <i className="fa-solid fa-upload" /> Choisir
                                <input
                                    type="file"
                                    accept={EXTENSIONS.map((e) => '.' + e).join(',')}
                                    disabled={!modifiable || occupe}
                                    onChange={(e) => {
                                        const f = e.target.files?.[0];
                                        e.target.value = '';
                                        if (f) lireFichier(f);
                                    }}
                                />
                            </label>
                        </div>
                    )}

                    {/* ── La prévisualisation ── */}
                    {etape === 'apercu' && analyse && (
                        <Previsualisation
                            analyse={analyse}
                            apercu={apercu}
                            mode={mode}
                            fichierNom={fichierNom}
                            feuilles={feuilles}
                            feuille={feuille}
                            surFeuille={analyserClasseur}
                            anneeLibelle={anneeActive?.libelle}
                            occupe={occupe}
                            modifiable={modifiable}
                            detailsOuverts={detailsOuverts}
                            surDetails={() => setDetailsOuverts((v) => !v)}
                            semestre={semestre}
                            surSemestre={changerSemestre}
                            surImporter={importer}
                            surAnnuler={recommencer}
                        />
                    )}

                    {/* ── Le bilan ── */}
                    {etape === 'bilan' && bilan && (
                        <>
                            <Bilan bilan={bilan} />
                            <div className="etu-import-actions">
                                <button type="button" className="etu-btn etu-btn-neutre" onClick={recommencer}>
                                    <i className="fa-solid fa-rotate-left" />{' '}
                                    {mode === 'coller' ? 'Coller un autre tableau' : 'Importer un autre fichier'}
                                </button>
                            </div>
                        </>
                    )}

                    {etape === 'depot' && bilan && <Bilan bilan={bilan} dernier />}

                    {/* ── LE BANDEAU DE GARANTIE, permanent ──
                        Forme de la maquette, et il reste affiché en toutes
                        circonstances : c'est une promesse sur ce que l'import NE fait
                        pas, donc elle ne doit pas disparaître au moment d'importer. */}
                    <div className="etu-alerte etu-alerte-garantie">
                        <i className="fa-solid fa-shield-halved" />
                        <span>
                            Cet import ne modifie <strong>que les deux rangs venus de SCODOC</strong> :
                            le rang à l'admission et le rang dans la promotion. Les champs d'APOGEE —
                            nom, statut, boursier, redoublant, classement — ne sont jamais écrasés, et
                            aucun étudiant n'est créé. Un numéro que la clé de liaison ne retrouve pas
                            est ignoré, pas ajouté.
                        </span>
                    </div>

                </div>
            </div>

            {/* ── Historique ──
                CINQ colonnes, et aucune pour « Créés » : la maquette n'en dessine pas,
                et c'est la lecture la plus courte de ce que fait cet import. */}
            <div className="etu-carte">
                <div className="etu-carte-entete">
                    <i className="fa-solid fa-clock-rotate-left" /> Historique SCODOC Rang
                </div>
                {historique.length === 0 ? (
                    <div className="etu-table-vide">Aucun import SCODOC pour ce département</div>
                ) : (
                    <table className="etu-table etu-table-histo">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Mode</th>
                                <th className="etu-col-nb" title="Mis à jour">
                                    <i className="fa-solid fa-rotate" />
                                </th>
                                <th className="etu-col-nb" title="Ignorés — appariés, sans classement à apporter">
                                    <i className="fa-solid fa-minus" />
                                </th>
                                <th className="etu-col-nb" title="Non trouvés — cas normal">
                                    <i className="fa-solid fa-circle-question etu-nb-non-trouves" />
                                </th>
                                <th className="etu-col-nb" title="Erreurs">
                                    <i className="fa-solid fa-triangle-exclamation" />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {historique.map((h) => (
                                <tr
                                    key={h.id}
                                    className={bilan?.import_id === h.id ? 'etu-ligne-active' : ''}
                                    onClick={() => { setBilan(bilanDepuisJournal(h)); setEtape('depot'); }}
                                    title="Afficher le bilan de cet import"
                                >
                                    <td>
                                        <div>{dateHeure(h.created_at)}</div>
                                        <div className="etu-histo-fichier">
                                            <i className={'fa-solid ' + (h.mode === 'coller' ? 'fa-clipboard' : 'fa-file-excel')} />
                                            {h.fichier_nom || 'collage'}
                                            {h.auteur ? ' · ' + h.auteur : ''}
                                        </div>
                                    </td>
                                    <td className="etu-histo-mode">{h.mode}</td>
                                    <td className="etu-col-nb etu-nb-majs">{h.nb_mis_a_jour}</td>
                                    <td className="etu-col-nb etu-nb-ignores">{h.nb_ignores}</td>
                                    <td className="etu-col-nb etu-nb-non-trouves">{h.nb_non_trouves}</td>
                                    <td className="etu-col-nb etu-nb-erreurs">{h.nb_erreurs}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

// ═════════════════════════════════════════════════════════════════════════════
// LA PRÉVISUALISATION — les deux temps, comme APOGEE
//
// L'ordre n'est pas discutable : le bilan et le bouton D'ABORD, la vérification
// colonne par colonne ensuite. L'inverse poussait l'action principale hors de
// l'écran, défaut trouvé à la relecture de l'image au lot 1.2 et reproduit au 1.3.
// ═════════════════════════════════════════════════════════════════════════════
function Previsualisation({
    analyse, apercu, mode, fichierNom, feuilles, feuille, surFeuille, anneeLibelle,
    occupe, modifiable, detailsOuverts, surDetails, surImporter, surAnnuler,
    semestre, surSemestre
}) {
    const enCollage = mode === 'coller';
    const colonnes = analyse.colonnes.filter((c) => c.trouvee);
    // Ce que l'import ÉCRIRAIT : les lignes appariées, inscrites, dont le classement
    // n'est pas protégé — que la valeur change ou non. Ni les ignorées ni les non
    // trouvées n'écrivent quoi que ce soit.
    const aImporter = apercu ? apercu.nb_a_ecrire + apercu.nb_identiques : 0;

    // ⚠️ Le semestre n'est exigé que si le fichier apporte un rang DE PROMOTION. Un
    // export de recrutement n'en a pas, et le réclamer serait réclamer une information
    // que l'utilisateur n'a pas. Le moteur local le dit (`exigeSemestre`), le serveur
    // le redit (`semestre_exige`) — et c'est lui qui refuse l'écriture.
    const semestreExige = !!(analyse.exigeSemestre || apercu?.semestre_exige);
    const semestreManquant = semestreExige && !semestre;

    const nonTrouves = apercu
        ? apercu.details.filter((d) => d.etat === 'inconnu' || d.etat === 'sans_inscription')
        : [];
    const verrous = apercu ? apercu.verrous : [];

    return (
        <div className="etu-apercu">

            <div className="etu-apercu-entete etu-apercu-scodoc">
                <i className="fa-solid fa-eye" />
                <strong>{enCollage ? 'Tableau collé' : (fichierNom || 'fichier')}</strong>
                {!enCollage && feuilles.length > 1 && (
                    <>
                        <span className="etu-apercu-sep">·</span>
                        <span>feuille</span>
                        <select value={feuille} onChange={(e) => surFeuille(e.target.value)} disabled={occupe}>
                            {feuilles.map((f) => <option key={f} value={f}>{f}</option>)}
                        </select>
                    </>
                )}
                <span className="etu-apercu-sep">·</span>
                <span className="etu-apercu-compte">
                    {analyse.nbLignesUtiles} ligne{analyse.nbLignesUtiles > 1 ? 's' : ''} de données,
                    dont {analyse.nbAvecValeur} avec un rang
                </span>
                <span className="etu-apercu-annee">
                    <i className="fa-solid fa-calendar" /> {anneeLibelle || '—'}
                </span>
            </div>

            {analyse.bloquant && (
                <div className="etu-statut etu-statut-ko etu-statut-bloc">
                    <i className="fa-solid fa-circle-exclamation" />
                    <span>{analyse.bloquant}</span>
                </div>
            )}

            {/* TEMPS 1 — ce que l'import ferait, et le bouton */}
            {!analyse.bloquant && apercu && (
                <>
                    {/* ── LE SEMESTRE DU RELEVÉ ──
                        Il n'apparaît que quand le fichier apporte un rang de promotion.
                        Et il est DEMANDÉ, jamais deviné : le déduire des noms de colonnes
                        du relevé (`R4.01`, `UE41`) serait le devineur que le §0.8
                        interdit, et se tromperait le jour où la scolarité exporte un
                        semestre avec les intitulés d'un autre. */}
                    {semestreExige && (
                        <div className={'etu-alerte ' + (semestreManquant ? 'etu-alerte-warning' : 'etu-alerte-info')}>
                            <i className={'fa-solid ' + (semestreManquant ? 'fa-circle-question' : 'fa-calendar-check')} />
                            <span className="etu-semestre-choix">
                                <strong>Ce relevé concerne le semestre :</strong>
                                <select
                                    value={semestre}
                                    onChange={(e) => surSemestre(e.target.value)}
                                    disabled={!modifiable || occupe}
                                >
                                    <option value="">— à choisir</option>
                                    {[1, 2, 3, 4, 5, 6].map((x) => (
                                        <option key={x} value={x}>S{x}</option>
                                    ))}
                                </select>
                                {semestreManquant
                                    ? <em>Un rang de promotion change à chaque semestre : sans lui,
                                        « 12 / 41 » serait un nombre que personne ne peut dater.</em>
                                    : <em>Il sera enregistré avec le rang, et s'affichera sur la fiche.</em>}
                            </span>
                        </div>
                    )}

                    <div className="etu-compteurs">
                        <div className="etu-compteur">
                            <div className="etu-compteur-nb etu-nb-majs">{apercu.nb_a_ecrire}</div>
                            <div className="etu-compteur-label">À écrire</div>
                        </div>
                        <div className="etu-compteur">
                            <div className="etu-compteur-nb etu-nb-identiques">{apercu.nb_identiques}</div>
                            <div className="etu-compteur-label">Inchangés</div>
                        </div>
                        <div className="etu-compteur">
                            <div className="etu-compteur-nb etu-nb-ignores">{apercu.nb_ignores}</div>
                            <div className="etu-compteur-label">Ignorés</div>
                        </div>
                        <div className="etu-compteur">
                            <div className="etu-compteur-nb etu-nb-non-trouves">{apercu.nb_non_trouves}</div>
                            <div className="etu-compteur-label">Non trouvés</div>
                        </div>
                        <div className="etu-compteur">
                            <div className="etu-compteur-nb etu-nb-erreurs">{apercu.nb_refuses}</div>
                            <div className="etu-compteur-label">Refusés</div>
                        </div>
                    </div>

                    <div className="etu-import-actions">
                        <button
                            type="button"
                            className="etu-btn etu-btn-scodoc"
                            disabled={!modifiable || occupe || aImporter === 0 || semestreManquant}
                            title={semestreManquant
                                ? 'Choisis le semestre du relevé : un rang de promotion sans son semestre est indatable.'
                                : aImporter === 0
                                    ? "Aucune ligne n'apporterait de rang : rien à écrire."
                                    : undefined}
                            onClick={surImporter}
                        >
                            <i className="fa-solid fa-file-import" />{' '}
                            Importer {aImporter} ligne{aImporter > 1 ? 's' : ''}
                        </button>
                        <button type="button" className="etu-btn etu-btn-neutre" onClick={surAnnuler} disabled={occupe}>
                            <i className="fa-solid fa-xmark" /> Annuler
                        </button>
                        {(apercu.details.length > 0 || analyse.refus.length > 0) && (
                            <button type="button" className="etu-btn etu-btn-neutre" onClick={surDetails}>
                                <i className={'fa-solid ' + (detailsOuverts ? 'fa-chevron-up' : 'fa-chevron-down')} />{' '}
                                Détail ligne à ligne
                            </button>
                        )}
                    </div>

                    {/* Le rang maximum de la campagne : le dénominateur du « 34 / 210 »
                        de la fiche. Il est annoncé ICI parce qu'il s'écrit sur toutes
                        les lignes touchées, et qu'un nombre qui apparaît sur une fiche
                        sans avoir jamais été montré à l'import est un nombre suspect. */}
                    {/* Le dénominateur, annoncé AVANT d'écrire — un nombre qui apparaît
                        sur une fiche sans avoir jamais été montré à l'import est un nombre
                        suspect. Il y en a un par champ, et les deux ne suivent pas la même
                        règle : celui du recrutement décrit une campagne et ne descend
                        jamais, celui de la promotion décrit un semestre et se refait. */}
                    {Object.entries(apercu.max_fichier || {}).map(([cle, max]) => (
                        <div className="etu-aide etu-aide-bloc" key={cle}>
                            <i className="fa-solid fa-ranking-star" />{' '}
                            {libelleChamp(cle)} — rang le plus élevé du fichier : <strong>{max}</strong>.
                            C'est le dénominateur qui s'affichera sur la fiche (« … / {max} »).{' '}
                            {cle === 'classement_recrutement'
                                ? 'Il ne descend jamais : un extrait partiel ne rétrécira pas la campagne.'
                                : 'Chaque semestre est un classement neuf : celui-ci suit le relevé.'}
                        </div>
                    ))}

                    {/* LES CADENAS QUI VONT JOUER. C'est le seul endroit où on les voit
                        avant d'écrire, et c'est la raison d'être de cette page. */}
                    {verrous.length > 0 && (
                        <div className="etu-alerte etu-alerte-garantie">
                            <i className="fa-solid fa-lock" />
                            <span>
                                {verrous.length} rang{verrous.length > 1 ? 's' : ''} protégé
                                {verrous.length > 1 ? 's' : ''} par un cadenas : le fichier ne l'écrasera
                                pas. Pour qu'il reprenne la main, ouvrir le cadenas sur la fiche.{' '}
                                {verrous.slice(0, 3).map(
                                    (v) => `n° ${v.code_etudiant} (${libelleChamp(v.champ)})`
                                ).join(', ')}
                                {verrous.length > 3 ? '…' : ''}
                            </span>
                        </div>
                    )}

                    {/* Les non trouvés en ORANGE, et la phrase dit que c'est normal. */}
                    {nonTrouves.length > 0 && (
                        <div className="etu-alerte etu-alerte-warning">
                            <i className="fa-solid fa-circle-question" />
                            <span>
                                {nonTrouves.length} ligne{nonTrouves.length > 1 ? 's' : ''} sans inscrit
                                correspondant. <strong>C'est un cas normal</strong> : SCODOC exporte le
                                recrutement, et tous les candidats admis ne s'inscrivent pas. Ces lignes
                                seront ignorées, aucun étudiant ne sera créé.
                            </span>
                        </div>
                    )}

                    {analyse.refus.length > 0 && (
                        <div className="etu-alerte etu-alerte-warning">
                            <i className="fa-solid fa-triangle-exclamation" />
                            <span>
                                {analyse.refus.length} ligne{analyse.refus.length > 1 ? 's' : ''} refusée
                                {analyse.refus.length > 1 ? 's' : ''} avant l'envoi : numéro absent ou mal
                                formé, doublon dans le fichier, ou classement hors bornes. Le détail
                                ligne à ligne les nomme.
                            </span>
                        </div>
                    )}

                    {/* Les cibles nommées par le mapping que le moteur ne sait pas lire.
                        Muet, ce cas ferait croire à un fichier incomplet. */}
                    {analyse.ciblesInconnues.length > 0 && (
                        <div className="etu-alerte etu-alerte-warning">
                            <i className="fa-solid fa-circle-question" />
                            <span>
                                Correspondance{analyse.ciblesInconnues.length > 1 ? 's' : ''} inutilisable
                                {analyse.ciblesInconnues.length > 1 ? 's' : ''} :{' '}
                                {analyse.ciblesInconnues.map((c) => `« ${c.intitule} » → ${c.cible} (${c.motif})`).join(' · ')}.
                                À corriger dans l'onglet Paramétrage.
                            </span>
                        </div>
                    )}

                    {detailsOuverts && (
                        <div className="etu-detail-lignes">
                            {analyse.refus.map((r, i) => (
                                <div className="etu-detail-ligne etu-detail-refus" key={'r' + i}>
                                    <i className="fa-solid fa-circle-xmark" />
                                    <span className="etu-detail-num">L.{r.ligne}</span>
                                    <span>{r.message}</span>
                                </div>
                            ))}
                            {apercu.details.map((d, i) => (
                                <div
                                    className={'etu-detail-ligne '
                                        + (d.etat === 'modifie' ? 'etu-detail-modif'
                                            : d.etat === 'verrou' ? 'etu-detail-protege'
                                                : 'etu-detail-avert')}
                                    key={'d' + i}
                                >
                                    <i className={'fa-solid ' + (
                                        d.etat === 'modifie' ? 'fa-rotate'
                                            : d.etat === 'verrou' ? 'fa-lock'
                                                : d.etat === 'identique' ? 'fa-equals'
                                                    : 'fa-circle-question'
                                    )} />
                                    <span className="etu-detail-num">L.{d.ligne}</span>
                                    <span>
                                        n° {d.code_etudiant}
                                        {d.nom ? ` — ${d.nom} ${d.prenom}` : ''}
                                        {d.etat === 'modifie' && ` : ${d.avant === null ? 'vide' : d.avant} → ${d.apres}`}
                                        {d.etat === 'identique' && ` : ${d.apres} (inchangé)`}
                                        {d.message ? ` — ${d.message}` : ''}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* TEMPS 2 — la vérification colonne par colonne (§0.8) */}
            {colonnes.length > 0 && (
                <>
                    <div className="etu-apercu-titre">
                        <i className="fa-solid fa-table-columns" /> Colonnes reconnues
                        <span className="etu-chip-compte">
                            {colonnes.length} sur {analyse.entetes.filter((h) => String(h ?? '').trim() !== '').length}
                        </span>
                    </div>
                    <div className="etu-table-cadre etu-table-cadre-scodoc etu-cadre-colonnes">
                        <table className="etu-table etu-table-scodoc">
                            <thead>
                                <tr>
                                    <th>Colonne du fichier</th>
                                    <th>→ Champ</th>
                                    <th className="etu-col-taux">Conforme</th>
                                    <th>Ce qui sera écrit</th>
                                </tr>
                            </thead>
                            <tbody>
                                {colonnes.map((c) => (
                                    <tr key={c.cle} className={c.refusee ? 'etu-ligne-refusee' : ''}>
                                        <td className="etu-mono">
                                            <i className={'fa-solid '
                                                + (c.refusee ? 'fa-circle-xmark etu-refus'
                                                    : c.nbNonVides === 0 ? 'fa-circle-minus etu-neutre'
                                                        : 'fa-circle-check etu-conforme')} />{' '}
                                            {c.intituleSource}
                                            {c.role === 'cle' && <span className="etu-tag-cle">clé</span>}
                                        </td>
                                        <td><span className="etu-badge-champ">{c.cible}</span></td>
                                        <td className="etu-col-taux">
                                            {c.taux === null
                                                ? '— · 0'
                                                : `${Math.round(c.taux * 100)} % · ${c.nbNonVides}`}
                                            {c.refusee && (
                                                <div className="etu-refus-motif">
                                                    refusée — {c.nbNonConformes} valeur
                                                    {c.nbNonConformes > 1 ? 's' : ''} qui ne {c.nbNonConformes > 1 ? 'sont' : 'est'} pas {c.forme}
                                                </div>
                                            )}
                                        </td>
                                        <td className="etu-apercu-exemples">
                                            {c.nbNonVides === 0
                                                ? <span className="etu-aide">vide</span>
                                                : c.exemples.map((ex, k) => (
                                                    <span key={k} className={ex.converti ? 'etu-exemple-converti' : ''}
                                                        title={ex.converti ? `dans le fichier : ${ex.source}` : undefined}>
                                                        {ex.ecrit}{k < c.exemples.length - 1 ? ' · ' : ''}
                                                    </span>
                                                ))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Le §0.7 impose de TOLÉRER l'ajout de colonnes. Les nommer évite la
                        question « et mes notes de terminale, elles sont passées où ? ». */}
                    {analyse.nonReclamees.length > 0 && (
                        <div className="etu-aide etu-aide-bloc">
                            <i className="fa-solid fa-shield-halved" />{' '}
                            {analyse.nonReclamees.length} colonne
                            {analyse.nonReclamees.length > 1 ? 's' : ''} non lue
                            {analyse.nonReclamees.length > 1 ? 's' : ''} : aucune correspondance ne les
                            désigne, donc <strong>rien de leur contenu n'entre en base</strong>. Le
                            fichier est lu dans le navigateur.
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// ═════════════════════════════════════════════════════════════════════════════
// LE BILAN — quatre compteurs (écart E39)
// ═════════════════════════════════════════════════════════════════════════════
function Bilan({ bilan, dernier }) {
    const anomalies = bilan.anomalies || [];
    const bloquantes = anomalies.filter((a) => !MOTIFS_AVERTISSEMENT.includes(a.motif));
    const avertissements = anomalies.filter((a) => MOTIFS_AVERTISSEMENT.includes(a.motif));

    return (
        <div className="etu-bilan">
            <div className={'etu-bilan-entete etu-bilan-scodoc' + (bilan.nb_erreurs ? ' avec-erreurs' : '')}>
                <i className={'fa-solid ' + (bilan.nb_erreurs ? 'fa-circle-exclamation' : 'fa-circle-check')} />
                <span>
                    {dernier && <span className="etu-bilan-dernier">Dernier import — </span>}
                    {dateHeure(bilan.date_import)}
                    {bilan.fichier_nom ? ' — ' + bilan.fichier_nom : ' — collage'}
                    {bilan.semestre ? ' — semestre S' + bilan.semestre : ''}
                </span>
            </div>

            <div className="etu-compteurs">
                <div className="etu-compteur">
                    <div className="etu-compteur-nb etu-nb-majs">{bilan.nb_mis_a_jour}</div>
                    <div className="etu-compteur-label">Mis à jour</div>
                </div>
                <div className="etu-compteur">
                    <div className="etu-compteur-nb etu-nb-ignores">{bilan.nb_ignores}</div>
                    <div className="etu-compteur-label">Ignorés</div>
                </div>
                <div className="etu-compteur">
                    <div className="etu-compteur-nb etu-nb-non-trouves">{bilan.nb_non_trouves}</div>
                    <div className="etu-compteur-label">Non trouvés</div>
                </div>
                <div className="etu-compteur">
                    <div className="etu-compteur-nb etu-nb-erreurs">{bilan.nb_erreurs}</div>
                    <div className="etu-compteur-label">Erreurs</div>
                </div>
            </div>

            {/* TOUTES les anomalies, dans une zone qui défile : une anomalie cachée est
                une anomalie qu'on ne corrige pas. Les avertissements — non trouvés,
                sans inscription, cadenas — en ORANGE, jamais en rouge. */}
            {(bloquantes.length > 0 || avertissements.length > 0) && (
                <div className="etu-bilan-anomalies">
                    {bloquantes.map((a, i) => (
                        <div className="etu-detail-ligne etu-detail-refus" key={'b' + i}>
                            <i className="fa-solid fa-triangle-exclamation" />
                            <span className="etu-detail-num">L.{a.ligne}</span>
                            <span>{a.message}</span>
                        </div>
                    ))}
                    {avertissements.map((a, i) => (
                        <div className="etu-detail-ligne etu-detail-avert" key={'w' + i}>
                            <i className={'fa-solid ' + (a.motif === 'verrou_actif' ? 'fa-lock' : 'fa-circle-question')} />
                            <span className="etu-detail-num">L.{a.ligne}</span>
                            <span>{a.message}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/** Une ligne du journal relue comme un bilan — `detail_erreurs` est déjà en base. */
function bilanDepuisJournal(ligne) {
    return {
        import_id: ligne.id,
        date_import: ligne.created_at,
        fichier_nom: ligne.fichier_nom,
        mode: ligne.mode,
        nb_mis_a_jour: ligne.nb_mis_a_jour,
        nb_ignores: ligne.nb_ignores,
        nb_non_trouves: ligne.nb_non_trouves,
        nb_erreurs: ligne.nb_erreurs,
        anomalies: Array.isArray(ligne.detail_erreurs) ? ligne.detail_erreurs : []
    };
}

export default ImportScodoc;
